import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2, FolderKanban, Inbox, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import EmployerPageFrame from "../components/employer/EmployerPageFrame";

const workspaces = [
  {
    title: "Neue Anzeige",
    note: "Berufssuche, Pflichtfelder, GPT-Vorschlaege sowie Draft und Publish laufen in einer breiten Create-Flaeche.",
    href: "/dashboard/stb/create",
    cta: "Anzeige anlegen",
    icon: FilePlus2,
  },
  {
    title: "Verwalten",
    note: "Filter, Lifecycle-Aktionen und Vorschau bleiben gebuendelt in einem lesbaren Anzeigenarbeitsraum.",
    href: "/dashboard/stb/manage",
    cta: "Anzeigen steuern",
    icon: FolderKanban,
  },
  {
    title: "Bewerbungen",
    note: "Lean gehaltene Bewerbungspruefung mit Decision Summary, optionaler WT-Summary und interner Entscheidungsvorbereitung.",
    href: "/dashboard/stb/applications",
    cta: "Bewerbungen sichten",
    icon: Inbox,
  },
];

const controlSignals = [
  { label: "Template-Stand", value: "CoreUI React", note: "Employer nutzt dieselbe technische Shell-Basis wie Applicant und Admin." },
  { label: "Arbeitsmodus", value: "Hiring Funnel", note: "Anlegen, Verwalten und Bewerbungen bleiben die primaeren Arbeitsachsen." },
  { label: "Scope", value: "Germany-Core", note: "Kein Matching, kein Ranking und kein Decision Cockpit in diesem Pass." },
];

const guardrails = [
  "Keine zweite Theme-Welt fuer Employer. Tokens kommen weiterhin aus src/theme/dashboardTokens.js.",
  "Keine neue Businesslogik. Bestehende APIs, Rechte und Payloads bleiben unveraendert.",
  "Keine Rohdokumente und keine PII-Ausweitung im Employer-Pfad.",
];

export default function EmployerDashboard() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Experts4Europe | Hiring Control Deck";
  }, []);

  return (
    <EmployerPageFrame
      title="Hiring Control Deck"
      subtitle="Employer arbeitet jetzt als durchgehender Full-Width-Arbeitsraum fuer Anzeige, Verwaltung und Bewerbungen statt als Mischung aus Dashboard-Resten und Einzelseiten."
      actions={(
        <>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/stb/manage")}>
            Verwalten
          </Button>
          <Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={() => navigate("/dashboard/stb/applications")}>
            Bewerbungen
          </Button>
          <Button className="dashboard-action-button" onClick={() => navigate("/dashboard/stb/create")}>
            Neue Anzeige
          </Button>
        </>
      )}
      contentClassName="space-y-4"
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_320px]">
        <section className="dashboard-mission-board">
          <div className="dashboard-section-head">
            <div>
              <span className="dashboard-kicker">Hiring Core</span>
              <h2 className="dashboard-section-title">Operativer Funnel statt Showcase-Dashboard</h2>
              <p className="dashboard-section-copy">
                Die Hauptflaeche bleibt auf konkrete Arbeitsschritte fokussiert: Anzeige anlegen, Bestand steuern und Bewerbungen in derselben Shell sichten.
              </p>
            </div>
          </div>

          <div className="dashboard-summary-grid dashboard-summary-grid-employer">
            {controlSignals.map((item, index) => (
              <article key={item.label} className={`dashboard-summary-card${index === 0 ? " dashboard-summary-card-highlight" : ""}`}>
                <span className="dashboard-summary-label">{item.label}</span>
                <strong>{item.value}</strong>
                <p>{item.note}</p>
              </article>
            ))}
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            {workspaces.map((item, index) => {
              const Icon = item.icon;
              return (
              <article key={item.title} className="dashboard-step-card">
                <div className="dashboard-step-icon" aria-hidden="true">
                  <Icon size={17} strokeWidth={2} />
                </div>
                <div className="dashboard-step-copy">
                  <div className="dashboard-step-head">
                    <h3>{item.title}</h3>
                    <span className="dashboard-pill dashboard-pill-soft">{index === 0 ? "Create" : index === 1 ? "Manage" : "Review"}</span>
                  </div>
                  <p>{item.note}</p>
                </div>
                <Button variant="outline" className="dashboard-inline-button" onClick={() => navigate(item.href)}>
                  {item.cta}
                </Button>
              </article>
            )})}
          </div>
        </section>

        <aside className="dashboard-secondary-rail">
          <section className="dashboard-rail-card">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Control Room</span>
                <h2 className="dashboard-section-title">Die drei Bewegungen</h2>
              </div>
            </div>
            <div className="dashboard-action-stack">
              {workspaces.map((item, index) => (
                <button
                  key={item.title}
                  type="button"
                  className={`dashboard-stack-button${index === 0 ? " is-primary" : ""}`}
                  onClick={() => navigate(item.href)}
                >
                  <span>{item.cta}</span>
                  <span aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          </section>

          <section className="dashboard-rail-card">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Guardrails</span>
                <h2 className="dashboard-section-title">Kein Scope-Drift</h2>
              </div>
            </div>
            <div className="space-y-3">
              {guardrails.map((item) => (
                <div key={item} className="dashboard-note">
                  <ShieldCheck size={16} strokeWidth={2} />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>

      <section className="dashboard-card-shell">
        <div className="dashboard-section-head">
          <div>
            <span className="dashboard-kicker">Route Inventory</span>
            <h2 className="dashboard-card-title">Bereits in derselben Employer-Shell</h2>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 text-sm">
          {[
            "/dashboard/stb/create",
            "/dashboard/stb/manage",
            "/dashboard/stb/preview/:id",
            "/dashboard/stb/applications",
            "/dashboard/stb/edit/:id",
            "/dashboard/stb/verwaltung",
            "/dashboard/stb/anzeigen",
            "/dashboard/stb/analyse/:id",
          ].map((route) => (
            <div key={route} className="dashboard-surface">
              <span className="dashboard-summary-label">Employer Route</span>
              <strong>{route}</strong>
              <p>Laeuft jetzt unter derselben CoreUI-React-Vollflaechenlogik.</p>
            </div>
          ))}
        </div>
      </section>
    </EmployerPageFrame>
  );
}
